import React, { useState } from 'react';
import { Typography, Dialog, Container, Box, Fade, Grid } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import img01 from './assets/img01.jpg';
import img02 from './assets/img02.jpg';
import img03 from './assets/img03.jpg';
import img04 from './assets/img04.jpg';
import img05 from './assets/img05.jpg';
import img06 from './assets/img06.jpg';
import img07 from './assets/img07.jpg';
import img08 from './assets/img08.jpg';
import img09 from './assets/img09.jpg';
import img10 from './assets/img10.jpg';
import img11 from './assets/img11.jpg';
import img12 from './assets/img12.jpg';

const images = [
  { src: img01, alt: 'Reception Area', height: 'tall' },
  { src: img02, alt: 'Garden', height: 'medium' },
  { src: img03, alt: 'Off-sreet Parking', height: 'short' },
  { src: img04, alt: 'Front Entrance', height: 'medium' },
  { src: img05, alt: 'Passage Seating Area', height: 'tall' },
  { src: img06, alt: 'Friendly Staff', height: 'short' },
  { src: img07, alt: 'Fynbos Garden', height: 'medium' },
  { src: img08, alt: 'Full range of services', height: 'tall' },
  { src: img09, alt: 'Office', height: 'short' },
  { src: img10, alt: 'Large Treatment Rooms', height: 'medium' },
  { src: img11, alt: 'Colsultation Rooms', height: 'tall' },
  { src: img12, alt: 'Secure Office', height: 'short' },
];
const MasonryContainer = styled(Box)(({ theme }) => ({
  columnCount: 4,
  columnGap: '16px',
  [theme.breakpoints.down('lg')]: {
    columnCount: 3,
  },
  [theme.breakpoints.down('md')]: {
    columnCount: 2,
  },
  [theme.breakpoints.down('sm')]: {
    columnCount: 1,
  },
}));

const MasonryItem = styled(Box)(({ theme, height }) => {
  const getHeight = () => {
    switch (height) {
      case 'short': return '200px';
      case 'medium': return '280px';
      case 'tall': return '360px';
      default: return '280px';
    }
  };

  return {
    breakInside: 'avoid',
    marginBottom: '16px',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    height: getHeight(),
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-8px) scale(1.02)',
      boxShadow: `0 20px 40px ${theme.palette.primary.main}30`,
      '& img': {
        transform: 'scale(1.1)',
      },
    },
  };
});

const StyledImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'transform 0.4s ease',
});



function About() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const theme = useTheme();

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handleNextImage = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
    setCurrentImageIndex(nextIndex);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        paddingTop: '120px',
        paddingBottom: '80px',
        background: 'linear-gradient(135deg, rgba(14, 84, 109, 0.05) 0%, rgba(22, 178, 139, 0.05) 100%)',
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Fade in={true} timeout={1000}>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              About Us
            </Typography>
          </Box>
        </Fade>

        {/* Content Section */}
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '32px',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  lineHeight: 1.7,
                  fontSize: '1.2rem'
                }}
              >
                Constantia MediSuites is a private medical centre situated in a serene, secure location opposite Mediclinic Constantiaberg. Our doctors and health practitioners are all established in their respective fields and value relationships with new and long-term patients.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '32px',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  lineHeight: 1.7,
                  fontSize: '1.2rem'
                }}
              >
                The MediSuites offer a peaceful environment with an open, light feel and modern amenities. There is a tranquil garden and the property is child- and wheelchair friendly with ample on-site parking. Our reception and support staff look forward to welcoming you.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Gallery Section */}
      <Container maxWidth="xl" sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            mb: 4
          }}
        >
          Our Facilities
        </Typography>
        <MasonryContainer>
          {images.map((image, index) => (
            <MasonryItem
              key={index}
              height={image.height}
              onClick={() => handleImageClick(image, index)}
            >
              <StyledImage
                src={image.src}
                alt={image.alt}
                loading="lazy"
              />

            </MasonryItem>
          ))}
        </MasonryContainer>
      </Container>

      <Dialog
        open={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        {selectedImage && (
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            style={{ width: '100%', height: 'auto', cursor: 'pointer', display: 'block' }}
            onClick={handleNextImage}
          />
        )}
      </Dialog>
    </Box>
  );
}

export default About;